/**
 * A single day's hour grid — renders Google Calendar busy blocks (read-only)
 * and Activity Blocks (draggable/clickable). Shared by desktop (6 cols) & mobile (1 col).
 */
import React from 'react';
import { CATEGORY_META } from './plannerConstants';

const HOUR_PX = 56;

function timeToOffset(dateObj, workStartHour) {
  const hours = dateObj.getHours() + dateObj.getMinutes() / 60;
  return Math.max(0, (hours - workStartHour) * HOUR_PX);
}

export function DayColumn({
  dayDate, blocks, events, workStartHour, workEndHour, selectedTaskId,
  onDropTask, onMoveBlock, onSlotClick, onOpenBlock, isToday,
}) {
  const hours = Array.from({ length: workEndHour - workStartHour }, (_, i) => workStartHour + i);
  const totalHeight = hours.length * HOUR_PX;

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e, hour) => {
    e.preventDefault();
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('application/json')); } catch { return; }
    const start = new Date(dayDate);
    start.setHours(hour, 0, 0, 0);
    if (payload.type === 'task') onDropTask(payload.id, start);
    if (payload.type === 'block') onMoveBlock(payload.id, start);
  };

  return (
    <div className="flex-1 min-w-0" data-testid={`day-column-${dayDate.toISOString().slice(0, 10)}`}>
      <div className={`text-center py-1.5 rounded-t-lg text-xs font-semibold ${isToday ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
        {dayDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
      </div>
      <div className="relative border border-border rounded-b-lg overflow-hidden" style={{ height: totalHeight }}>
        {hours.map((h) => (
          <div
            key={h}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, h)}
            onClick={() => selectedTaskId && onSlotClick(h)}
            data-testid={`day-slot-${dayDate.toISOString().slice(0, 10)}-${h}`}
            className={`absolute left-0 right-0 border-b border-border/50 ${selectedTaskId ? 'hover:bg-primary/10 cursor-pointer' : ''}`}
            style={{ top: (h - workStartHour) * HOUR_PX, height: HOUR_PX }}
          >
            <span className="text-[10px] text-muted-foreground/60 pl-1">{h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'am' : 'pm'}</span>
          </div>
        ))}

        {events.map((ev, i) => {
          const start = new Date(ev.start?.dateTime || ev.start?.date || dayDate);
          const end = new Date(ev.end?.dateTime || ev.end?.date || start);
          const top = timeToOffset(start, workStartHour);
          const height = Math.max(20, timeToOffset(end, workStartHour) - top);
          return (
            <div
              key={`ev-${i}`}
              className="absolute left-1 right-1 rounded-md bg-slate-400/25 border border-slate-400/40 px-1.5 py-0.5 text-[10px] text-slate-600 z-10 truncate"
              style={{ top, height }}
              title={ev.summary}
              data-testid={`gcal-event-${i}`}
            >
              {ev.summary || 'Busy'}
            </div>
          );
        })}

        {blocks.map((b) => {
          const start = new Date(b.start);
          const end = new Date(b.end);
          const top = timeToOffset(start, workStartHour);
          const height = Math.max(28, timeToOffset(end, workStartHour) - top);
          const meta = CATEGORY_META[b.activity_category] || { label: b.activity_category, color: '#64748b' };
          return (
            <div
              key={b.id}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify({ type: 'block', id: b.id }))}
              onClick={() => onOpenBlock(b)}
              onDragOver={handleDragOver}
              onDrop={(e) => {
                e.preventDefault();
                let payload;
                try { payload = JSON.parse(e.dataTransfer.getData('application/json')); } catch { return; }
                if (payload.type === 'task') onDropTask(payload.id, start, b);
              }}
              data-testid={`activity-block-${b.id}`}
              className="absolute left-1 right-1 rounded-md px-1.5 py-1 z-20 cursor-pointer shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              style={{ top, height, backgroundColor: `${meta.color}22`, borderLeft: `3px solid ${meta.color}` }}
            >
              <p className="text-[11px] font-semibold leading-tight truncate" style={{ color: meta.color }}>{meta.label}</p>
              <p className="text-[10px] text-muted-foreground truncate">{b.tasks?.length || 0} task{b.tasks?.length === 1 ? '' : 's'}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
